import React, { useState, useEffect } from "react";
import { Text, Box, Tag, HStack, Tooltip } from "@chakra-ui/react";
import { ScenarioAnalysis } from "build-ts/analyst";
import { FilterProto } from "build-ts/metadata";
import { getAnalyst } from "services/AnalystService";
import Table, { TableColumn, TablePagination } from "../common/Table";
import { getOperatorSymbol } from "utils/analysisUtils";
import { getFilterDisplayString } from "utils/nameUtils";

interface AnalystScenariosSelectionProps {
	selectedAnalystId?: string;
	selectedItems?: Set<string>;
	onSelectionChange?: (selectedItems: Set<string>) => void;
}

const AnalystScenariosSelection: React.FC<AnalystScenariosSelectionProps> = ({
	selectedAnalystId,
	selectedItems = new Set(),
	onSelectionChange,
}) => {
	const [scenarios, setScenarios] = useState<ScenarioAnalysis[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchScenarios = async (analystId: string) => {
		setIsLoading(true);
		setError(null);
		try {
		const response = await getAnalyst(analystId);
			if (response && response.scenarios) {
			setScenarios(response.scenarios);
		}
		} catch (err) {
			setError("Failed to fetch scenarios");
			console.error("Error fetching scenarios:", err);
		} finally {
		setIsLoading(false);
		}
	};

	useEffect(() => {
		if (selectedAnalystId) {
			fetchScenarios(selectedAnalystId);
		}
	}, [selectedAnalystId]);

	// Helper function to format a single filter
	const formatFilter = (filter: FilterProto): string => {
		let displayText = "";
		if (filter.date_filter) {
			displayText = `${filter.kg_column_header?.name}: ${getFilterDisplayString(filter)}`;
		} else if (filter.kg_column_header && filter.values && filter.values.length > 0) {
			const operatorSymbol = getOperatorSymbol(filter.operator!);
			displayText = `${filter.kg_column_header.name} ${operatorSymbol} ${filter.values.join(", ")}`;
		}
		return displayText;
	};

	// Helper function to render filters with hover for additional filters
	const renderFilters = (filters: FilterProto[]) => {
		if (!filters || filters.length === 0) {
			return <Text fontSize="sm" color="gray.400">No filters</Text>;
		}

		const visibleFilters = filters.slice(0, 3);
		const remainingFilters = filters.slice(3);

		return (
			<HStack spacing={2} flexWrap="wrap">
				{visibleFilters.map((filter, index) => {
					const displayText = formatFilter(filter);
					return displayText ? (
						<Tag key={index}
						size="sm"
						colorScheme="purple"
						fontSize="sm"
						bg="white"
						border="1px"
						borderColor="purple.700"
						color="purple.700"
						py={1}
						fontWeight="600"
						>
							{displayText}
						</Tag>
					) : null;
				})}

				{remainingFilters.length > 0 && (
					<Tooltip
						label={
							<Box>
								{remainingFilters.map((filter, index) => {
									const displayText = formatFilter(filter);
									return displayText ? (
										<Text key={index} fontSize="xs" mb={1}>
											{displayText}
										</Text>
									) : null;
								})}
							</Box>
						}
						placement="top"
						hasArrow
					>
						<Tag size="sm" colorScheme="blue" fontSize="xs" cursor="pointer">
							+{remainingFilters.length} more
						</Tag>
					</Tooltip>
				)}
			</HStack>
		);
	};

	// Table columns
	const columns: TableColumn<ScenarioAnalysis>[] = [
		{
			key: "issue",
			label: "Issue",
			sortable: true,
			width: "20%",
			render: (item) => (
				<Text fontSize="sm" fontWeight="600" mb={0} noOfLines={2}>
					{item.issue || "No issue description"}
				</Text>
			),
		},
		{
			key: "trigger",
			label: "Trigger",
			sortable: true,
			width: "50%",
			render: (item) => (
				<Text fontSize="sm" mb={0} noOfLines={2} whiteSpace="normal" wordBreak="break-word">
					{item.trigger || "No trigger specified"}
				</Text>
			),
		},
		{
			key: "preset_filters",
			label: "Applied Filters",
			sortable: false,
			width: "30%",
			render: (item) => renderFilters(item.preset_filters || []),
		},
	];

	// Handle selection change
	const handleSelectionChange = (newSelectedItems: Set<string>) => {
		if (onSelectionChange) {
			onSelectionChange(newSelectedItems);
		}
	};

	// Mock pagination (since we're not using real pagination)
	const pagination: TablePagination = {
		total: scenarios.length,
		offset: 0,
		has_more: false,
	};

	// Show message if no analyst is selected
	if (!selectedAnalystId) {
		return (
			<Box height="100%" overflowY="hidden" textAlign="center">
				<Text color="gray.500" mb={0}>
					Please select an analyst first
				</Text>
			</Box>
		);
	}

	return (
		<Box height="100%" overflowY="hidden">
			<Table
				data={scenarios}
				columns={columns}
				loading={isLoading}
				error={error}
				pagination={pagination}
				selectable={true}
				selectedItems={selectedItems}
				onSelectionChange={handleSelectionChange}
				getItemId={(item) => item.id || ""}
				emptyMessage="No scenarios found"
				emptySubMessage="This analyst does not have any scenarios configured"
				hideSelectionCount={true}
			/>
		</Box>
	);
};

export default AnalystScenariosSelection;